/**
 * IdentityGallery — single-glance display of customer + guarantor ID photos
 * directly on the CustomerProfile, no extra screens.
 *
 * Shows: Customer Photo, Customer CNIC F, Customer CNIC B,
 *        Guarantor 1 Photo, Guarantor 1 CNIC F, Guarantor 1 CNIC B,
 *        Guarantor 2 Photo, Guarantor 2 CNIC F, Guarantor 2 CNIC B (when G2 exists).
 *
 * Click any thumbnail to open full size in a lightbox.
 */
import React, { useState } from "react";
import { ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function Thumb({ url, label, onOpen, testId }) {
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      {url ? (
        <button
          type="button"
          onClick={() => onOpen(`${API_BASE}${url}`, label)}
          className="block w-full aspect-square overflow-hidden rounded-md border border-slate-200 hover:border-emerald-500 transition-colors"
        >
          <img src={`${API_BASE}${url}`} alt={label} className="w-full h-full object-cover" />
        </button>
      ) : (
        <div className="w-full aspect-square rounded-md border border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
          <ImageIcon className="w-5 h-5" />
          <span className="text-[10px] mt-0.5">Not uploaded</span>
        </div>
      )}
    </div>
  );
}

export default function IdentityGallery({ customer }) {
  const [view, setView] = useState({ open: false, src: "", label: "" });
  const c = customer || {};
  const cdocs = c.documents || {};
  const g1 = c.guarantor1 || {};
  const g1docs = g1.documents || {};
  const g2 = c.guarantor2;
  const g2docs = g2?.documents || {};

  const openLightbox = (src, label) => setView({ open: true, src, label });

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="identity-gallery">
      <div className="font-display font-semibold text-slate-900 mb-3">Photos & ID Documents</div>

      <div className="space-y-4">
        {/* Customer row */}
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-2">Customer · {c.name}</div>
          <div className="grid grid-cols-3 gap-3">
            <Thumb url={cdocs.photo} label="Customer Photo" onOpen={openLightbox} testId="ig-cust-photo" />
            <Thumb url={cdocs.cnic_front} label="CNIC Front" onOpen={openLightbox} testId="ig-cust-cnic-front" />
            <Thumb url={cdocs.cnic_back} label="CNIC Back" onOpen={openLightbox} testId="ig-cust-cnic-back" />
          </div>
        </div>

        {/* Guarantor 1 */}
        {g1.name && (
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Guarantor 1 · {g1.name}</div>
            <div className="grid grid-cols-3 gap-3">
              <Thumb url={g1docs.photo} label="Photo" onOpen={openLightbox} testId="ig-g1-photo" />
              <Thumb url={g1docs.cnic_front} label="CNIC Front" onOpen={openLightbox} testId="ig-g1-cnic-front" />
              <Thumb url={g1docs.cnic_back} label="CNIC Back" onOpen={openLightbox} testId="ig-g1-cnic-back" />
            </div>
          </div>
        )}

        {/* Guarantor 2 */}
        {g2?.name && (
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-2">Guarantor 2 · {g2.name}</div>
            <div className="grid grid-cols-3 gap-3">
              <Thumb url={g2docs.photo} label="Photo" onOpen={openLightbox} testId="ig-g2-photo" />
              <Thumb url={g2docs.cnic_front} label="CNIC Front" onOpen={openLightbox} testId="ig-g2-cnic-front" />
              <Thumb url={g2docs.cnic_back} label="CNIC Back" onOpen={openLightbox} testId="ig-g2-cnic-back" />
            </div>
          </div>
        )}
      </div>

      <Dialog open={view.open} onOpenChange={(o) => setView((v) => ({ ...v, open: o }))}>
        <DialogContent className="max-w-3xl p-2" data-testid="ig-lightbox">
          <VisuallyHidden>
            <DialogTitle>{view.label || "Photo Preview"}</DialogTitle>
          </VisuallyHidden>
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 px-2 pt-2">{view.label}</div>
          {view.src && (
            <img src={view.src} alt={view.label} className="w-full max-h-[80vh] object-contain rounded-md" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
